import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Paper,
  Stack,
  RadioGroup,
  Radio,
  FormControlLabel,
  Typography,
  Box,
  Divider,
  Autocomplete,
  Chip,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'

const TONE_OPTIONS = [
  'warm', 'gentle', 'supportive', 'empathetic', 'encouraging', 'neutral', 'direct', 'extra_warm',
]
const SENSITIVITY_LEVELS = ['standard', 'elevated', 'high']
const ENGAGEMENT_TIERS = ['at_risk', 'low', 'moderate', 'engaged', 'highly_engaged']
const INTERVENTION_PHASES = ['onboarding', 'early', 'active', 'maintenance']
const TIME_OF_DAY_OPTIONS = ['morning', 'afternoon', 'evening']
const DAY_OF_WEEK_OPTIONS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const OPERATOR_LABELS = {
  equals: 'equals',
  not_equals: 'not equals',
  gt: '>',
  lt: '<',
  gte: '>=',
  lte: '<=',
  in: 'is one of',
  not_in: 'is none of',
  between: 'between',
  present: 'present',
  not_present: 'not present',
}

// Which operators apply to each risk-code value_format. Format-specific
// operators are listed first so the default operator on a fresh selection
// reflects the format (discrete → equals, numeric → equals, presence → present).
// present/not_present apply to every format and are appended at the end.
const RISK_FACTOR_OPERATORS_BY_FORMAT = {
  presence: ['present', 'not_present'],
  discrete: ['equals', 'not_equals', 'in', 'not_in', 'present', 'not_present'],
  numeric:  ['equals', 'not_equals', 'gt', 'lt', 'gte', 'lte', 'between', 'present', 'not_present'],
}

const CONDITION_TYPES = [
  {
    value: 'engagement_score',
    label: 'Engagement Score',
    description: 'Numeric engagement score (0-100)',
    operators: ['equals', 'gt', 'lt', 'gte', 'lte', 'between'],
    valueType: 'number',
  },
  {
    value: 'engagement_tier',
    label: 'Engagement Tier',
    description: 'Computed tier from engagement score',
    operators: ['equals', 'in'],
    valueType: 'select',
    options: ENGAGEMENT_TIERS,
  },
  {
    value: 'risk_factor',
    label: 'Risk Factor',
    description: 'Match on a patient\'s current risk factor; operator and value controls depend on the risk code format.',
    // Operators are format-dependent; computed at render time.
    operators: ['present', 'not_present', 'equals', 'not_equals', 'in', 'not_in', 'gt', 'lt', 'gte', 'lte', 'between'],
    valueType: 'risk_factor',
  },
  {
    value: 'intervention_day',
    label: 'Intervention Day',
    description: 'Days since intervention enrollment',
    operators: ['equals', 'gt', 'lt', 'gte', 'lte', 'between'],
    valueType: 'number',
  },
  {
    value: 'intervention_phase',
    label: 'Intervention Phase',
    description: 'Current phase of intervention',
    operators: ['equals', 'in'],
    valueType: 'select',
    options: INTERVENTION_PHASES,
  },
  {
    value: 'time_of_day',
    label: 'Time of Day',
    description: 'Current time bucket',
    operators: ['equals', 'in'],
    valueType: 'select',
    options: TIME_OF_DAY_OPTIONS,
  },
  {
    value: 'day_of_week',
    label: 'Day of Week',
    description: 'Current day of the week',
    operators: ['equals', 'in'],
    valueType: 'multiselect',
    options: DAY_OF_WEEK_OPTIONS,
  },
  {
    value: 'days_since_last_session',
    label: 'Days Since Last Session',
    description: 'Number of days since last completed session',
    operators: ['equals', 'gt', 'lt', 'gte', 'lte'],
    valueType: 'number',
  },
  {
    value: 'completion_rate',
    label: 'Completion Rate',
    description: 'Track completion rate (0-100)',
    operators: ['gt', 'lt', 'gte', 'lte'],
    valueType: 'number',
  },
  {
    value: 'current_streak',
    label: 'Current Streak',
    description: 'Current consecutive session streak',
    operators: ['equals', 'gt', 'lt', 'gte', 'lte'],
    valueType: 'number',
  },
]

const ACTION_TYPES = [
  {
    value: 'priority_boost',
    label: 'Modify Priority',
    fields: [
      { key: 'value', label: 'Priority Delta', type: 'number', required: true, helperText: 'Positive to raise priority, negative to lower (e.g., 30 or -10)' },
      { key: 'track_codes', label: 'Pathways', type: 'track_picker', required: true, helperText: 'Select pathways whose priority should be modified' },
    ],
    validate: (action) => {
      const hasTracks = action.track_codes && action.track_codes.length > 0
      if (!hasTracks) return 'Select at least one pathway'
      return null
    },
  },
  {
    value: 'set_tone',
    label: 'Set Tone',
    fields: [
      { key: 'value', label: 'Tone', type: 'select', options: TONE_OPTIONS, required: true },
    ],
  },
  {
    value: 'limit_tracks_per_session',
    label: 'Limit Pathways Per Session',
    fields: [
      { key: 'value', label: 'Max Pathways', type: 'number', required: true, helperText: 'e.g., 2' },
    ],
  },
  {
    value: 'mandatory_item',
    label: 'Require',
    fields: [
      { key: 'track_code', label: 'Pathway', type: 'single_track_picker', required: true, helperText: 'Select the pathway to require' },
    ],
  },
  {
    value: 'prompt_modifier',
    label: 'Prompt Modifier',
    fields: [
      { key: 'modifications.tone', label: 'Tone Override', type: 'select', options: TONE_OPTIONS },
      { key: 'modifications.preamble_addition', label: 'Preamble Addition', type: 'multiline', helperText: 'Text prepended to agent instructions' },
      { key: 'modifications.sensitivity_level', label: 'Sensitivity Level', type: 'select', options: SENSITIVITY_LEVELS },
      { key: 'modifications.avoid_phrases_add', label: 'Avoid Phrases', type: 'chips', helperText: 'Phrases the AI should avoid' },
      { key: 'modifications.monitor_for', label: 'Monitor For', type: 'chips', helperText: 'Topics to watch for and escalate' },
    ],
  },
]

/** Get a nested value from an object using a dot-separated key */
function getNestedValue(obj, key) {
  return key.split('.').reduce((acc, part) => acc?.[part], obj)
}

/** Set a nested value on an object using a dot-separated key, returning a new object */
function setNestedValue(obj, key, value) {
  const parts = key.split('.')
  if (parts.length === 1) {
    return { ...obj, [key]: value }
  }
  const [first, ...rest] = parts
  return {
    ...obj,
    [first]: setNestedValue(obj[first] || {}, rest.join('.'), value),
  }
}

function StrategyEditorDialog({
  open,
  onClose,
  strategy,
  onSave,
  saving,
  interventionTracks = [],
  interventionRiskCodes = [],
  allRiskCodes = [],
}) {
  // Build lookups for risk-code resolution + orphan detection.
  const interventionCodeSet = new Set(
    (interventionRiskCodes || []).map(rc => rc.code).filter(Boolean)
  )
  // Map for fast lookup of a risk code's full record (value_format etc.) by code.
  const allRiskCodeMap = new Map(
    (allRiskCodes || []).map(rc => [rc.code, rc])
  )
  // If interventionRiskCodes is the richer source (it's joined with risk_codes),
  // prefer it for in-intervention codes — overlay onto the all-codes map.
  for (const rc of interventionRiskCodes || []) {
    if (rc.code) allRiskCodeMap.set(rc.code, rc)
  }
  const isEditMode = !!strategy

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    priority_order: 1,
    is_active: true,
    strategy_definition: {
      match_mode: 'all',
      conditions: [],
      actions: [],
    },
  })

  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) {
      if (strategy) {
        setFormData({
          name: strategy.name || '',
          description: strategy.description || '',
          priority_order: strategy.priority_order || 1,
          is_active: strategy.is_active !== false,
          strategy_definition: strategy.strategy_definition || {
            match_mode: 'all',
            conditions: [],
            actions: [],
          },
        })
      } else {
        setFormData({
          name: '',
          description: '',
          priority_order: 1,
          is_active: true,
          strategy_definition: {
            match_mode: 'all',
            conditions: [],
            actions: [],
          },
        })
      }
      setError(null)
    }
  }, [open, strategy])

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const updateDefinition = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      strategy_definition: { ...prev.strategy_definition, [field]: value },
    }))
  }

  const addCondition = () => {
    const firstType = CONDITION_TYPES[0]
    updateDefinition('conditions', [
      ...formData.strategy_definition.conditions,
      { type: firstType.value, operator: firstType.operators[0], value: '' },
    ])
  }

  const updateCondition = (index, field, value) => {
    const conditions = [...formData.strategy_definition.conditions]
    conditions[index] = { ...conditions[index], [field]: value }

    // Reset dependent fields on type change
    if (field === 'type') {
      const typeDef = CONDITION_TYPES.find((ct) => ct.value === value)
      conditions[index].operator = typeDef?.operators[0] || 'equals'
      conditions[index].value = ''
      delete conditions[index].risk_code
    }

    // Reset value when operator changes to/from between
    if (field === 'operator') {
      const prevOp = formData.strategy_definition.conditions[index]?.operator
      if (value === 'between' && prevOp !== 'between') {
        conditions[index].value = ['', '']
      } else if (value !== 'between' && prevOp === 'between') {
        conditions[index].value = ''
      }
      // For 'in' operator, ensure value is array
      if ((value === 'in' || value === 'is_one_of') && !Array.isArray(conditions[index].value)) {
        conditions[index].value = []
      }
    }

    updateDefinition('conditions', conditions)
  }

  const removeCondition = (index) => {
    updateDefinition(
      'conditions',
      formData.strategy_definition.conditions.filter((_, i) => i !== index)
    )
  }

  const addAction = () => {
    const firstType = ACTION_TYPES[0]
    updateDefinition('actions', [
      ...formData.strategy_definition.actions,
      { type: firstType.value },
    ])
  }

  const updateAction = (index, key, value) => {
    const actions = [...formData.strategy_definition.actions]
    actions[index] = setNestedValue(actions[index], key, value)
    updateDefinition('actions', actions)
  }

  const updateActionType = (index, newType) => {
    const actions = [...formData.strategy_definition.actions]
    // Clear all fields except type when type changes
    actions[index] = { type: newType }
    updateDefinition('actions', actions)
  }

  const removeAction = (index) => {
    updateDefinition(
      'actions',
      formData.strategy_definition.actions.filter((_, i) => i !== index)
    )
  }

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Composition rule name is required')
      return
    }
    // Validate actions with custom validators
    for (const action of formData.strategy_definition.actions) {
      const typeDef = ACTION_TYPES.find((at) => at.value === action.type)
      if (typeDef?.validate) {
        const validationError = typeDef.validate(action)
        if (validationError) {
          setError(`${typeDef.label}: ${validationError}`)
          return
        }
      }
    }
    try {
      setError(null)
      await onSave(formData)
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save composition rule')
    }
  }

  const handleClose = () => {
    setError(null)
    onClose()
  }

  // Build the Autocomplete options list for risk-factor conditions. Always
  // includes the intervention-selected codes; also includes any currently-
  // referenced orphan code (flagged) so existing rules can still render.
  const buildRiskCodeOptions = (condition) => {
    const options = (interventionRiskCodes || []).map(rc => ({
      code: rc.code,
      label: rc.name || rc.code,
      risk: rc,
      orphan: false,
    }))
    if (condition.risk_code && !interventionCodeSet.has(condition.risk_code)) {
      const full = allRiskCodeMap.get(condition.risk_code)
      options.push({
        code: condition.risk_code,
        label: full?.name || condition.risk_code,
        risk: full || null,
        orphan: true,
      })
    }
    return options
  }

  // Risk Code picker for risk_factor conditions. Rendered *between* the
  // Condition Type dropdown and the Operator dropdown so the picked code's
  // format can drive the operator and value control choices.
  const renderRiskCodePicker = (condition, index) => {
    const options = buildRiskCodeOptions(condition)
    const selected = options.find(o => o.code === condition.risk_code) || null
    const isOrphan = !!selected?.orphan

    return (
      <Autocomplete
        size="small"
        options={options}
        value={selected}
        onChange={(_, newVal) => {
          const nextCode = newVal?.code || ''
          const nextRc = nextCode ? allRiskCodeMap.get(nextCode) : null
          const nextFmt = nextRc?.value_format
          const prevRc = allRiskCodeMap.get(condition.risk_code)
          const prevFmt = prevRc?.value_format
          const nextOps = RISK_FACTOR_OPERATORS_BY_FORMAT[nextFmt] || ['present', 'not_present']
          // If the format changed (or there was no prior format), pick the
          // first operator for the new format so the format-specific value
          // control renders. Within the same format, preserve the user's
          // operator choice when it's still valid.
          const formatChanged = prevFmt !== nextFmt
          const currentOp = condition.operator
          const nextOp = (!formatChanged && nextOps.includes(currentOp))
            ? currentOp
            : nextOps[0]
          const nextValue = (nextOp === 'in' || nextOp === 'not_in' || nextOp === 'between') ? [] : ''
          const conditions = [...formData.strategy_definition.conditions]
          conditions[index] = {
            ...conditions[index],
            risk_code: nextCode,
            operator: nextOp,
            value: nextValue,
          }
          updateDefinition('conditions', conditions)
        }}
        getOptionLabel={(o) => o?.label || o?.code || ''}
        isOptionEqualToValue={(o, v) => o?.code === v?.code}
        renderOption={(props, option) => (
          <li {...props} key={option.code}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2">{option.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.code} · {option.risk?.value_format || '—'}
                </Typography>
              </Box>
              {option.orphan && (
                <Chip size="small" color="warning" label="not in intervention" sx={{ height: 18, fontSize: '0.625rem' }} />
              )}
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Risk Code"
            error={isOrphan}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isOrphan && (
                    <Tooltip title="This risk code is not attached to this intervention. Re-add it in the Risk Codes tab, or change this rule to use a different code.">
                      <WarningIcon fontSize="small" color="warning" sx={{ mr: 0.5 }} />
                    </Tooltip>
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{ minWidth: 220 }}
      />
    )
  }

  // Render only the value control for a risk_factor condition; the risk code
  // picker is rendered separately (left of the operator).
  const renderRiskFactorInput = (condition, index) => {
    const rc = allRiskCodeMap.get(condition.risk_code)
    const format = rc?.value_format
    return renderRiskFactorValueControl(condition, index, format)
  }

  const renderRiskFactorValueControl = (condition, index, format) => {
    const op = condition.operator

    // No value for presence / not_present
    if (op === 'present' || op === 'not_present') return null

    const code = condition.risk_code
    const rc = allRiskCodeMap.get(code)

    if (!rc || format === 'presence') {
      // Unknown format or presence code with non-presence operator.
      // Show a disabled hint; the inline warning badge communicates the issue.
      return (
        <TextField
          size="small"
          label="Value"
          value={condition.value ?? ''}
          disabled
          sx={{ minWidth: 150 }}
        />
      )
    }

    if (format === 'discrete') {
      const allowed = Array.isArray(rc.discrete_values) ? rc.discrete_values : []
      if (op === 'in' || op === 'not_in') {
        const current = Array.isArray(condition.value) ? condition.value : []
        return (
          <Autocomplete
            multiple
            size="small"
            options={allowed}
            value={current}
            onChange={(_, newVal) => updateCondition(index, 'value', newVal)}
            renderTags={(value, getTagProps) =>
              value.map((opt, i) => (
                <Chip label={opt} size="small" {...getTagProps({ index: i })} key={opt} />
              ))
            }
            renderInput={(params) => <TextField {...params} label="Values" />}
            sx={{ minWidth: 200 }}
          />
        )
      }
      return (
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Value</InputLabel>
          <Select
            value={condition.value || ''}
            label="Value"
            onChange={(e) => updateCondition(index, 'value', e.target.value)}
          >
            {allowed.map(v => (
              <MenuItem key={v} value={v}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )
    }

    if (format === 'numeric') {
      if (op === 'between') {
        const bv = Array.isArray(condition.value) ? condition.value : ['', '']
        return (
          <>
            <TextField
              size="small"
              type="number"
              label="Min"
              value={bv[0] ?? ''}
              onChange={(e) => updateCondition(index, 'value', [e.target.value, bv[1]])}
              inputProps={{ min: rc.numeric_min, max: rc.numeric_max }}
              sx={{ width: 100 }}
            />
            <Typography variant="body2" sx={{ mx: 0.5 }}>to</Typography>
            <TextField
              size="small"
              type="number"
              label="Max"
              value={bv[1] ?? ''}
              onChange={(e) => updateCondition(index, 'value', [bv[0], e.target.value])}
              inputProps={{ min: rc.numeric_min, max: rc.numeric_max }}
              sx={{ width: 100 }}
            />
          </>
        )
      }
      return (
        <TextField
          size="small"
          type="number"
          label={`Value (${rc.numeric_min}–${rc.numeric_max})`}
          value={condition.value ?? ''}
          onChange={(e) => updateCondition(index, 'value', e.target.value)}
          inputProps={{ min: rc.numeric_min, max: rc.numeric_max }}
          sx={{ minWidth: 180 }}
        />
      )
    }

    return null
  }

  // Top-level condition value renderer. Dispatches to risk-factor-specific
  // helpers for the risk_factor type; falls through to generic controls for
  // number/select/multiselect/text conditions.
  const renderConditionValueInput = (condition, index) => {
    const typeDef = CONDITION_TYPES.find((ct) => ct.value === condition.type)
    if (!typeDef) return null

    const { operator } = condition

    if (typeDef.valueType === 'risk_factor') {
      return renderRiskFactorInput(condition, index)
    }

    // present/not_present on non-risk-factor types: no value needed
    if (operator === 'present' || operator === 'not_present') {
      return null
    }

    // between: two number fields
    if (operator === 'between') {
      const betweenVal = Array.isArray(condition.value) ? condition.value : ['', '']
      return (
        <>
          <TextField
            label="Min"
            size="small"
            type="number"
            value={betweenVal[0] ?? ''}
            onChange={(e) => updateCondition(index, 'value', [e.target.value, betweenVal[1]])}
            sx={{ width: 90 }}
          />
          <Typography variant="body2" sx={{ mx: 0.5 }}>to</Typography>
          <TextField
            label="Max"
            size="small"
            type="number"
            value={betweenVal[1] ?? ''}
            onChange={(e) => updateCondition(index, 'value', [betweenVal[0], e.target.value])}
            sx={{ width: 90 }}
          />
        </>
      )
    }

    // in operator or multiselect type: Autocomplete with multiple
    if (operator === 'in' || operator === 'is_one_of' || typeDef.valueType === 'multiselect') {
      const options = typeDef.options || []
      const currentValue = Array.isArray(condition.value) ? condition.value : []
      return (
        <Autocomplete
          multiple
          size="small"
          options={options}
          value={currentValue}
          onChange={(_, newVal) => updateCondition(index, 'value', newVal)}
          renderTags={(value, getTagProps) =>
            value.map((option, i) => (
              <Chip label={option} size="small" {...getTagProps({ index: i })} key={option} />
            ))
          }
          renderInput={(params) => <TextField {...params} label="Values" />}
          sx={{ flexGrow: 1, minWidth: 180 }}
        />
      )
    }

    // select: single select from options
    if (typeDef.valueType === 'select' && typeDef.options) {
      return (
        <FormControl size="small" sx={{ flexGrow: 1, minWidth: 150 }}>
          <InputLabel>Value</InputLabel>
          <Select
            value={condition.value || ''}
            label="Value"
            onChange={(e) => updateCondition(index, 'value', e.target.value)}
          >
            {typeDef.options.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt.replace(/_/g, ' ')}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )
    }

    // number
    if (typeDef.valueType === 'number') {
      return (
        <TextField
          label="Value"
          size="small"
          type="number"
          value={condition.value ?? ''}
          onChange={(e) => updateCondition(index, 'value', e.target.value)}
          sx={{ flexGrow: 1 }}
        />
      )
    }

    // fallback: text
    return (
      <TextField
        label="Value"
        size="small"
        value={condition.value ?? ''}
        onChange={(e) => updateCondition(index, 'value', e.target.value)}
        sx={{ flexGrow: 1 }}
      />
    )
  }

  const renderActionFields = (action, actionIndex) => {
    const typeDef = ACTION_TYPES.find((at) => at.value === action.type)
    if (!typeDef || !typeDef.fields) return null

    return (
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {typeDef.fields.map((field) => {
          const fieldValue = getNestedValue(action, field.key)

          switch (field.type) {
            case 'number':
              return (
                <TextField
                  key={field.key}
                  label={field.label}
                  size="small"
                  type="number"
                  value={fieldValue ?? ''}
                  onChange={(e) => updateAction(actionIndex, field.key, e.target.value)}
                  helperText={field.helperText}
                  required={field.required}
                  fullWidth
                />
              )

            case 'text':
              return (
                <TextField
                  key={field.key}
                  label={field.label}
                  size="small"
                  value={fieldValue ?? ''}
                  onChange={(e) => updateAction(actionIndex, field.key, e.target.value)}
                  helperText={field.helperText}
                  required={field.required}
                  fullWidth
                />
              )

            case 'multiline':
              return (
                <TextField
                  key={field.key}
                  label={field.label}
                  size="small"
                  multiline
                  rows={3}
                  value={fieldValue ?? ''}
                  onChange={(e) => updateAction(actionIndex, field.key, e.target.value)}
                  helperText={field.helperText}
                  fullWidth
                />
              )

            case 'select':
              return (
                <FormControl key={field.key} size="small" fullWidth>
                  <InputLabel>{field.label}</InputLabel>
                  <Select
                    value={fieldValue || ''}
                    label={field.label}
                    onChange={(e) => updateAction(actionIndex, field.key, e.target.value)}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {(field.options || []).map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt.replace(/_/g, ' ')}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )

            case 'multiselect':
              return (
                <Autocomplete
                  key={field.key}
                  multiple
                  size="small"
                  options={field.options || []}
                  value={Array.isArray(fieldValue) ? fieldValue : []}
                  onChange={(_, newVal) => updateAction(actionIndex, field.key, newVal)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, i) => (
                      <Chip label={option.replace(/_/g, ' ')} size="small" {...getTagProps({ index: i })} key={option} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label={field.label} helperText={field.helperText} />
                  )}
                />
              )

            case 'chips': {
              const chipValues = Array.isArray(fieldValue) ? fieldValue : []
              return (
                <Autocomplete
                  key={field.key}
                  multiple
                  freeSolo
                  size="small"
                  options={[]}
                  value={chipValues}
                  onChange={(_, newVal) => updateAction(actionIndex, field.key, newVal)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, i) => (
                      <Chip label={option} size="small" {...getTagProps({ index: i })} key={option} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label={field.label} helperText={field.helperText} placeholder="Type and press Enter" />
                  )}
                />
              )
            }

            case 'track_picker': {
              const selectedCodes = Array.isArray(fieldValue) ? fieldValue : []
              const trackOptions = interventionTracks.map((t) => ({
                code: t.code,
                label: `${t.name} (${t.code})`,
                orphan: false,
              }))
              // Preserve any selected codes that are no longer attached to the
              // intervention so they render (flagged) instead of disappearing.
              for (const code of selectedCodes) {
                if (!trackOptions.some((o) => o.code === code)) {
                  trackOptions.push({ code, label: code, orphan: true })
                }
              }
              const selectedOptions = trackOptions.filter((o) => selectedCodes.includes(o.code))
              return (
                <Autocomplete
                  key={field.key}
                  multiple
                  size="small"
                  options={trackOptions}
                  value={selectedOptions}
                  onChange={(_, newVal) => updateAction(actionIndex, field.key, newVal.map((v) => v.code))}
                  getOptionLabel={(option) => option.label || option.code || ''}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                  renderOption={(props, option) => (
                    <li {...props} key={option.code}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                          {option.label}
                        </Typography>
                        {option.orphan && (
                          <Chip size="small" color="warning" label="not in intervention" sx={{ height: 18, fontSize: '0.625rem' }} />
                        )}
                      </Box>
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, i) => (
                      <Chip
                        label={option.label}
                        size="small"
                        color={option.orphan ? 'warning' : 'default'}
                        icon={option.orphan ? <WarningIcon sx={{ fontSize: 14 }} /> : undefined}
                        {...getTagProps({ index: i })}
                        key={option.code}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label={field.label} helperText={field.helperText} />
                  )}
                />
              )
            }

            case 'single_track_picker': {
              const trackOptions = interventionTracks.map((t) => ({
                code: t.code,
                label: `${t.name} (${t.code})`,
                orphan: false,
              }))
              let selectedOption = trackOptions.find((o) => o.code === fieldValue) || null
              if (!selectedOption && fieldValue) {
                selectedOption = { code: fieldValue, label: fieldValue, orphan: true }
                trackOptions.push(selectedOption)
              }
              const isOrphan = !!selectedOption?.orphan
              return (
                <Autocomplete
                  key={field.key}
                  size="small"
                  options={trackOptions}
                  value={selectedOption}
                  onChange={(_, newVal) => updateAction(actionIndex, field.key, newVal?.code || '')}
                  getOptionLabel={(option) => option.label || option.code || ''}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                  renderOption={(props, option) => (
                    <li {...props} key={option.code}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                          {option.label}
                        </Typography>
                        {option.orphan && (
                          <Chip size="small" color="warning" label="not in intervention" sx={{ height: 18, fontSize: '0.625rem' }} />
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={field.label}
                      helperText={field.helperText}
                      required={field.required}
                      error={isOrphan}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isOrphan && (
                              <Tooltip title="This pathway is not attached to this intervention. Re-add it in the Pathways tab, or change this rule to use a different pathway.">
                                <WarningIcon fontSize="small" color="warning" sx={{ mr: 0.5 }} />
                              </Tooltip>
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )
            }

            default:
              return null
          }
        })}
      </Box>
    )
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditMode ? `Edit Composition Rule: ${strategy?.name}` : 'Create Composition Rule'}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Composition Rule Details */}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem' }}>
          Composition Rule Details
        </Typography>
        <Box display="grid" gridTemplateColumns="120px 1fr" gap={1.5} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">Name</Typography>
          <TextField
            size="small"
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            required
          />

          <Typography variant="body2" color="text.secondary">Description</Typography>
          <TextField
            size="small"
            value={formData.description}
            onChange={(e) => updateField('description', e.target.value)}
            multiline
            rows={2}
          />

        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Conditions */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem' }}>
              Conditions (When to Apply)
            </Typography>
            <RadioGroup
              row
              value={formData.strategy_definition.match_mode || 'all'}
              onChange={(e) => updateDefinition('match_mode', e.target.value)}
            >
              <FormControlLabel value="all" control={<Radio size="small" />} label="Match ALL" />
              <FormControlLabel value="any" control={<Radio size="small" />} label="Match ANY" />
            </RadioGroup>
          </Box>
          <Button size="small" startIcon={<AddIcon />} onClick={addCondition}>
            Add Condition
          </Button>
        </Box>

        {formData.strategy_definition.conditions.map((condition, index) => {
          const typeDef = CONDITION_TYPES.find((ct) => ct.value === condition.type)

          // For risk_factor, operator list depends on the selected code's format.
          let operatorList = typeDef?.operators || ['equals']
          let isOrphan = false
          if (typeDef?.valueType === 'risk_factor') {
            const rc = allRiskCodeMap.get(condition.risk_code)
            const fmt = rc?.value_format
            operatorList = RISK_FACTOR_OPERATORS_BY_FORMAT[fmt] || ['present', 'not_present']
            isOrphan = condition.risk_code && !interventionCodeSet.has(condition.risk_code)
          }

          return (
            <Paper key={index} sx={{ p: 2, mb: 1 }} variant="outlined">
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" sx={{ minWidth: 40 }}>
                  {index === 0 ? 'When' : formData.strategy_definition.match_mode === 'all' ? 'AND' : 'OR'}
                </Typography>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Condition Type</InputLabel>
                  <Select
                    value={condition.type}
                    label="Condition Type"
                    onChange={(e) => updateCondition(index, 'type', e.target.value)}
                  >
                    {CONDITION_TYPES.map((ct) => (
                      <MenuItem key={ct.value} value={ct.value}>
                        {ct.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {typeDef?.valueType === 'risk_factor' && renderRiskCodePicker(condition, index)}
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Operator</InputLabel>
                  <Select
                    value={condition.operator}
                    label="Operator"
                    onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                  >
                    {operatorList.map((op) => (
                      <MenuItem key={op} value={op}>
                        {OPERATOR_LABELS[op] || op.replace(/_/g, ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {renderConditionValueInput(condition, index)}
                {isOrphan && (
                  <Tooltip title="This risk code is not attached to this intervention. Re-add it in the Risk Codes tab, or change this rule to use a different code.">
                    <Chip
                      size="small"
                      color="warning"
                      icon={<WarningIcon sx={{ fontSize: 14 }} />}
                      label="not in intervention"
                      sx={{ height: 22, fontSize: '0.6875rem' }}
                    />
                  </Tooltip>
                )}
                <IconButton onClick={() => removeCondition(index)} size="small">
                  <DeleteIcon />
                </IconButton>
              </Stack>
              {typeDef?.description && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {typeDef.description}
                </Typography>
              )}
            </Paper>
          )
        })}

        {formData.strategy_definition.conditions.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No conditions defined. Rule will always apply when attached to an intervention.
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Actions */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem' }}>
            Actions (What to Do)
          </Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addAction}>
            Add Action
          </Button>
        </Box>

        {formData.strategy_definition.actions.map((action, index) => {
          // Detect orphan pathway references at the action level so we can
          // surface a warning chip in the header even if the user hasn't
          // opened the track picker.
          const interventionCodes = new Set((interventionTracks || []).map((t) => t.code))
          const referencedCodes = []
          if (Array.isArray(action.track_codes)) referencedCodes.push(...action.track_codes)
          if (action.track_code) referencedCodes.push(action.track_code)
          const orphanCodes = referencedCodes.filter((c) => c && !interventionCodes.has(c))

          return (
            <Paper key={index} sx={{ p: 2, mb: 1 }} variant="outlined">
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2" sx={{ minWidth: 40 }}>
                  {index === 0 ? 'Then' : 'AND'}
                </Typography>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Action Type</InputLabel>
                  <Select
                    value={action.type}
                    label="Action Type"
                    onChange={(e) => updateActionType(index, e.target.value)}
                  >
                    {ACTION_TYPES.map((at) => (
                      <MenuItem key={at.value} value={at.value}>
                        {at.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {orphanCodes.length > 0 && (
                  <Tooltip title={`${orphanCodes.length > 1 ? 'These pathways are' : 'This pathway is'} not attached to this intervention: ${orphanCodes.join(', ')}. Re-add to the intervention, or update this rule.`}>
                    <Chip
                      size="small"
                      color="warning"
                      icon={<WarningIcon sx={{ fontSize: 14 }} />}
                      label={`${orphanCodes.length} pathway${orphanCodes.length > 1 ? 's' : ''} not in intervention`}
                      sx={{ height: 22, fontSize: '0.6875rem' }}
                    />
                  </Tooltip>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <IconButton onClick={() => removeAction(index)} size="small">
                  <DeleteIcon />
                </IconButton>
              </Stack>
              {renderActionFields(action, index)}
            </Paper>
          )
        })}

        {formData.strategy_definition.actions.length === 0 && (
          <Alert severity="warning">
            No actions defined. Add at least one action for this composition rule to have an effect.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Composition Rule'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default StrategyEditorDialog
